import React, { useState } from 'react';
import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import useDebounce from 'constants/helper/useDebounce';
import CostumeNotificationForm from './costumeNotificationForm';
import { useCustomNotificationList } from './hooks/useCustomNotificationList';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomNotificationDelete } from './hooks/useCustomNotificationDelete';
import Swal from 'sweetalert2';

const CustomNotification = () => {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [selectedId, setSelectedId] = useState(null);
  const queryClient = useQueryClient();

  const handleSearch = (value) => setSearchString(value);
  const { customNotification, totalPages, isLoading } =
    useCustomNotificationList(page, debouncedSearch);
  const formatDate = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const tableData = customNotification?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
    createdAt: formatDate(item.createdAt),
  }));

  const { mutate: deleteNotification } = useCustomNotificationDelete();
  const swalBackground = useColorModeValue('#ffffff', '#1A202C');
  const swalColor = useColorModeValue('#000000', '#ffffff');

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This notification will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#319795',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      background: swalBackground,
      color: swalColor,
      borderRadius: '15px',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteNotification(id, {
          onSuccess: () => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Notification has been deleted successfully.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false,
              background: swalBackground,
              color: swalColor,
            });

            queryClient.invalidateQueries(['customNotificationList']);
          },
        });
      }
    });
  };

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },
            {
              key: 'name',
              label: 'Title',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'details',
              label: 'Message',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'createdAt',
              label: 'Date',
              width: '200px',
              headerSize: '14px',
            },
          ]}
          page={page}
          totalPages={totalPages}
          loading={isLoading}
          onPageChange={setPage}
          showTopAction={true}
          topAction={{
            title: 'Custom Notifications',
            label: 'Add Notification',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showSearch={true}
          onSearch={handleSearch}
          showAddBtn={true}
          showViewToggle={false}
          onEdit={(id) => {
            setSelectedId(id);
            setShowForm(true);
          }}
          onDelete={(row) => handleDelete(row._id)}
        />
      )}

      {showForm && (
        <Box w="100%">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mb={4}
            ml={3}
          >
            <Text fontSize="22px" fontWeight="bold">
              Custom Notification
            </Text>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForm(false);
                setSelectedId(null);
              }}
            >
              Back
            </Button>
          </Flex>

          <CostumeNotificationForm
            setSelectedId={setSelectedId}
            editId={selectedId}
            onSuccess={() => {
              queryClient.invalidateQueries(['customNotificationList']);
              setShowForm(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CustomNotification;
